import type { CountryCode } from "libphonenumber-js/max";

import { blueMui } from "@ryde/theme";

import { LoginAuthType, RideStatusEnum, TilesServiceEnum } from "../enums";
import type {
  RydeUiUnit,
  RydeUiUnitDropdownCacheKey,
  SupportedLanguage,
  TBulkFormPrefix,
  TTimeHHmm,
} from "../models";
import type {
  IAddressSearchBounds,
  ICoordinate,
} from "../models/common.models";
import type { TLatLngExpression } from "../models/map.models";

export const VEHICLE_LICENSE_EXPIRY_LIMIT = 30;
export const LANG_DEFAULT: SupportedLanguage = "en";
export const LANGS_SUPPORTED: SupportedLanguage[] = ["en", "he"];
export const LANG_HEADER_NAME = "app-language";
export const DEFAULT_PAGE_SIZE = 20;
export const TABLE_TRUNCATE_LEN = 20;

// Map / polyline drawing
export const DRAWING_POLYLINE_COLOR = blueMui._700;
export const OSRM_MAX_POINTS_FOR_SNAP_MATCHING = 100;

/** 100% — for percentage math (e.g. period-over-period change). */
export const MAX_PERCENT = 100;
/** Minimum GPS points to draw a tracking trail segment or polyline (start + end). */
export const MIN_TRACKING_POINTS_FOR_POLYLINE = 2;

export const defaultMapCenter: ICoordinate = {
  lat: 32.08529876373817,
  lng: 34.7817676,
};
export const GEO_CORDS_MATCH_PRECISION = 6;
export const MAX_AREA_WAYPOINTS_PER_DIRECTION = 20;
export const HYBRID_SPA_ROOT = "/react.asp#";
export const MAX_TABLE_SORTING_POPOVER_ITEMS = 5;
export const TABLE_SORTING_POPOVER_TRUNC_LENGTH = 15;

export const TABLE_PAGE_SIZE_GROUPING = 10;
export const TABLE_PAGE_SIZE_UNGROUPED = 50;
/** How many server-side group headers are loaded per virtual-list chunk (passengers). */
export const GROUP_VALUES_PAGE_SIZE = 20;
export const TABLE_PAGE_SIZE_REPORTS = 20;

// User actions history drawer + persisted list (`localStorage`)
export const USER_ACTIONS_HISTORY_STORAGE_KEY_PREFIX = "actions_history_v1_";
export const USER_ACTIONS_HISTORY_DRAWER_WIDTH_PX = 495;
export const HELP_TOPIC_DRAWER_WIDTH_PX = 780;
export const USER_ACTIONS_HISTORY_DRAWER_PAGE_SIZE = 20;
/** Cap for persisted recent actions per user+company in `localStorage`. */
export const USER_ACTIONS_HISTORY_MAX_STORED_ITEMS = 40;
/** Skip recording the same drawer again if it fires within this window (ms). */
export const USER_ACTIONS_HISTORY_DRAWER_DEDUPLICATE_WINDOW_MS = 400;

export const TABLE_PADDING_TOP = 15;
export const TABLE_GROUP_TOGGLE_HEIGHT = 32;
export const TABLE_HEADER_HEIGHT = 37;
export const TABLE_STANDARD_ROW_HEIGHT = 42;
export const TABLE_LAST_ROW_HEIGHT = 43;
export const TABLE_PAGINATION_HEIGHT = 52;
export const TABLE_PADDING_BOTTOM = 15;
export const LAYOUT_HEADER_HEIGHT = 153;
export const DEFAULT_GROUPED_TABLE_HEIGHT =
  TABLE_PADDING_TOP +
  TABLE_GROUP_TOGGLE_HEIGHT +
  TABLE_HEADER_HEIGHT +
  (TABLE_PAGE_SIZE_GROUPING - 1) * TABLE_STANDARD_ROW_HEIGHT +
  TABLE_LAST_ROW_HEIGHT +
  TABLE_PAGINATION_HEIGHT +
  TABLE_PADDING_BOTTOM;
export const TABLE_COLLAPSED_HEIGHT = 45;

export const RIGHTBAR_DEFAULT_WIDTH = "700px";
export const RIGHTBAR_DEFAULT_WIDTH_MOBILE = "95vw";
export const RIGHTBAR_RIDES_WIDTH = "1300px";
export const RIGHTBAR_COURSES_WIDTH = "1300px";
export const RIGHTBAR_JOURNEYS_WIDTH = "1300px";
export const RIGHTBAR_STATION_ROUTE_WIDTH = "1300px";
export const RIGHTBAR_ROLES_WIDTH = "1000px";
export const FORM_FIELD_ADDITIONAL_BUTTON_WIDTH = "48px";
export const USE_GENERATED_FAKE_DATA_FOR_RIDES = false;
export const USE_GENERATED_FAKE_DATA_FOR_BOOKINGS = false;
export const GENERATED_FAKE_DATA_FOR_RIDES_COUNT = 1050;
export const TIME_SAVE_EMULATION_MODE = true;

export const MIN_DATE_BASE = "0001-01-01";
export const MIN_DATE_ISO: TDateISO = `${MIN_DATE_BASE}T00:00:00.000Z`;
export const MIN_DATE_VALUE = "0001-01-01T00:00:00Z" as TDateISO;
export const MAX_DATE_VALUE = "2050-01-01T00:00:00Z" as TDateISO;

export const METADATA_KEY = "metadata" as const;
export const ComputedDataKey = "computedData";
export const ComputedStationInfoKey = "stationInfos";
export const VirtualDataKey = "virtualData";
export const METADATA_KEY_AND_DOT = `${METADATA_KEY}.`;

export const ServerFilteredEntities: RydeUiUnit[] = [
  "ride",
  "booking",
  "report",
  "passenger",
  "auditLog",
  "aiConversation",
  "providerLog",
  "adminAction",
  "etlJobLog",
  "etlSchedule",
  "process",
  "busStation",
  "messagingLog",
  "systemManager",
  "supplierQuarantine",
  "geoArea",
  "backup",
  "webhookDelivery",
];

export const ClientSideDatesServerFilteredEntities: RydeUiUnit[] = [
  "feedback",
  "incident",
  "notification",
  "rideProduction",
];

/** Server pagination with grid grouping (`groupByField` / `groupByValues`). */
export const ServerPaginatedEntitiesWithGrouping: RydeUiUnit[] = ["passenger"];

export const ServerPaginatedEntities: RydeUiUnit[] = [
  "passenger",
  "auditLog",
  "aiConversation",
  "providerLog",
  "adminAction",
  "etlJobLog",
  "etlSchedule",
  "process",
  "busStation",
  "messagingLog",
  "supplierQuarantine",
  "geoArea",
  "backup",
  "webhookDelivery",
];

// in future this flag can be replaced by ServerPaginatedEntities
export const ServerSearchEntities: RydeUiUnit[] = [
  "auditLog",
  "messagingLog",
  "aiConversation",
  "providerLog",
  "adminAction",
  "process",
  "webhookDelivery",
];

/** Server-paginated grids whose free-text search box is sent as `where.search.$ilike` (includes grouped passenger). */
export const ServerGridTextSearchEntities: RydeUiUnit[] = [
  ...ServerSearchEntities,
  "passenger",
];

export const AllowTimeEditInMainPickerEntities: RydeUiUnit[] = [
  "auditLog",
  "mapCacheStat",
  "etlJobLog",
  "messagingLog",
  "aiConversation",
  "providerLog",
  "adminAction",
  "backup",
  "webhookDelivery",
];

export const RideEntities: RydeUiUnit[] = ["ride", "rideMap"];
export const EntitiesWithNoDropdownItems: RydeUiUnit[] = [
  "ride",
  "booking",
  "report",
  "stationRoute",
  "stationDistance",
  "dataUpload",
];
export const EntitiesWithNoManualAddNew: RydeUiUnit[] = [
  "adminTools",
  "aiConversation",
  "dataUpload",
  "mapCacheStat",
  "process",
  "stationRoute",
  "webhookDelivery",
];
export const EntitiesWithoutPreCreateForm: RydeUiUnit[] = ["feedback"];
export const EntitiesWithoutDuplicateCrudUi: RydeUiUnit[] = [
  "dataUpload",
  "driver",
  "systemManager",
];
export const EntitiesWithoutBulkActions: RydeUiUnit[] = [
  "dataUpload",
  "rideProduction",
];
export const EntitiesWithoutBulkDeleteAction: RydeUiUnit[] = ["notification"];
export const EntitiesWithoutGenericUpload: RydeUiUnit[] = [
  "booking",
  "dataUpload",
];
export const EntitiesWithAdminMode: RydeUiUnit[] = ["ride", "course"];

export const NO_GROUPING_QS_VALUE = "none";
export const CARD_FILTERS_QS_KEY = "cardFilters";
/** Prefix returned by `getGridFieldNameByControlId` when a control id cannot be resolved to a grid field. */
export const UNRESOLVED_GRID_FIELD_PREFIX = "unresolved_";
export const GroupingGridObjectNoGroups = [undefined];
export const GROUPING_GRID_NO_GROUPS = "_NO_GROUPS_UNIQUE";
/** Row path for live-map delay status column (`IRideTableDto.statusLate`). */
export const GROUPING_FIELD_LIVE_MAP = "statusLate";
export const GROUPING_FIELD_PRODUCE_BUTTON = "todayState";

export const MAX_ADMIN_TABLE_TEXT_LENGTH = 20;

export const VIEW_NAME_MAX_LENGTH = 255;
export const MIDNIGHT_TIME = "00:00";
export const MAIN_DATE_FORMAT = "DD.MM.YYYY";
export const MAIN_TIME_FORMAT = "HH:mm";
export const MAIN_TIME_FORMAT_SEC = "HH:mm:ss";
export const MAIN_TIME_FORMAT_SEC_MS = "HH:mm:ss.SSS";
export const MAIN_DATETIME_DELIMITER = " - ";
export const NO_VALUE_DISPLAY = "--";
/** Em dash used as a placeholder when a value is missing/unknown. */
export const NO_VALUE_DASH = "—";
export const BOOLEAN_YES_CELL_SIGN = "v";
export const BOOLEAN_NO_CELL_SIGN = "-";
/** Length of YYYY-MM-DD date string. */
export const ISO_DATE_LENGTH = 10;
export const DATE_FORMAT_YMD = "YYYY-MM-DD";
export const DATE_FORMAT_DMY = "DD/MM/YYYY";
export const DATE_FORMAT_WITH_TIME = `${MAIN_DATE_FORMAT} ${MAIN_TIME_FORMAT}`;
export const DATE_DD_MM_FORMAT = "DD.MM";
export const DATE_MM_DD_FORMAT_WITH_TIME = `${DATE_DD_MM_FORMAT} ${MAIN_TIME_FORMAT}`;
export const DATE_FORMAT_YMD_WITH_TIME = `${DATE_FORMAT_YMD} ${MAIN_TIME_FORMAT}`;
/** ETL run timestamps, shown in the summary card and last-run log viewer. */
export const ETL_RUN_DATE_TIME_FORMAT = "YYYY/MM/DD HH:mm:ss";
export const REFERENCE_DATE_YMD = "2000-01-01";
export const DATE_TIME_FORMAT_FILE_NAME = "DD-MM-YYYY_HH_mm_ss";
export const ZERO_TIME_HHmm: TTimeHHmm = "00:00";
export const TIME_END_DAY_HHmm: TTimeHHmm = "23:59";
export const MILLISECONDS_IN_SECOND = 1000;
export const HOURS_IN_DAY = 24;
export const FIRST_HOUR_OF_DAY = 0;
export const LAST_HOUR_OF_DAY = HOURS_IN_DAY - 1;
export const SECONDS_IN_MINUTE = 60;
export const MINUTES_IN_HOUR = 60;
export const SECONDS_IN_HOUR = SECONDS_IN_MINUTE * MINUTES_IN_HOUR;
export const DEFAULT_COLUMN_WIDTH = 210;
export const COLUMN_RESIZE = {
  minWidth: 40,
  keyboardStep: 10,
  /** Measured widths keep two decimals, enough to freeze a stretched table without shifting it. */
  measuredWidthPrecision: 100,
} as const;
export const VALIDATE_EMAIL_REGEXP = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i;
export const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const DEFAULT_WEEKEND_START_DAY = 5;
export const DEFAULT_WEEKEND_DURATION_DAYS = 2;
export const API_BASE = "/api/v1/core";
export const API_BASE_LANDINGS = "/api/v1/landings";
export const SPECIFIC_SEARCH_PATTERN = /^id:(.+)/;
export const SPECIFIC_SEARCH_PARSABLE_EXPRESSION = /^[\d,\s]+$/;
export const YELLOW_MARK_TYPING_DEBOUNCE_MS = 300;
export const SEARCH_TYPING_DEBOUNCE_MS = 300;
export const DATA_E2E_KEY = "data-e2e";

/** ISO-8601 UTC instant, e.g. 2026-07-28T21:00:00.000Z. Shared so the type guard and its callers cannot drift apart. */
export const ISO_DATE_TIME_UTC_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;
export const DATA_E2E_TEMPLATE_KEY = "data-e2e-template";
export const DATA_LPIGNORE_KEY = "data-lpignore";
export const DATA_FORM_TYPE_KEY = "data-form-type";
export const DATA_1P_IGNORE_KEY = "data-1p-ignore";

export const PASSWORD_GATE_INPUT_DATA_ATTRS = {
  [DATA_LPIGNORE_KEY]: "true",
  [DATA_FORM_TYPE_KEY]: "other",
  [DATA_1P_IGNORE_KEY]: "true",
} as const;
export const INITIAL_RIDE_STATIONS_COLLAPSED = false;
export const UTF_SPACE = "\u00A0";
export const PRICE_MAXIMUM_FRACTION_DIGITS = 2;
export const DEFAULT_TABLE_ROW_HEIGHT = 42;
export const ESTIMATED_KANBAN_ITEM_HEIGHT = 390;
export const LAYOUT_RIGHT_HEIGHT_WITHOUT_FOOTER = 56;
export const LAYOUT_RIGHT_HEIGHT_WITH_FOOTER = 110;
export const LAYOUT_RIGHT_HEIGHT_INCREASE_MOBILE = 30;
export const DRAWER_EDIT_FORM_IMAGE_WIDTH = "190px";
export const LAYOUT_RIGHT_IMAGE_AREA_WIDTH = "226px";
export const COLLAPSE_DURATION_SEC = 0.2;
export const COLLAPSE_CONTAINER_MAX_HEIGHT = 400;
export const EMPTY_VALUE_DISPLAY = "";
export const DRAG_HANDLE_WIDTH = 10;
export const SPLITTER_MAP_PASSENGERS_INITIAL_WIDTH = 780;
export const SPLITTER_MAP_INITIAL_WIDTH = 550;
export const SPLITTER_MAP_INITIAL_WIDTH_LIVE_MAP = 715;
export const SPLITTER_MAP_LEFT_LIMIT = 30;
export const RIDES_HEADER_HEIGHT_COMPUTED = "158px";
export const REPORTS_HEADER_HEIGHT_COMPUTED = "158px";
export const JWT_COOKIE_NAME = "JWT_TOKEN";
export const JWT_OLD_APP_INTERNAL_COOKIE_NAME = "jwt";
export const GRID_POLLING_INTERVAL = 60_000;
export const GRID_POLLING_INTERVAL_TEST = 10_000;
export const MAP_POLLING_INTERVAL = 60_000;
export const MAP_POLLING_INTERVAL_TEST = 10_000;
export const MAP_RESIZE_GLOBAL_EVENT_INTERVAL = 500;
export const MAP_ROUTING_MACHINE_INIT_TIMEOUT = 200;
export const PRODUCE_POLLING_INTERVAL_MS = 4_400;
/** Stop after this many consecutive status failures. At 4.4s the 14th check is ~57s after the first. */
export const PRODUCE_POLLING_MAX_CONSECUTIVE_ERRORS = 14;

/** Admin tools: default retention when clearing old produce log chunks. */
export const PRODUCE_OLD_LOGS_RETENTION_DAYS = 30;

/** Admin tools: minimum allowed retention period (days) for manual produce log cleanup. */
export const PRODUCE_OLD_LOGS_RETENTION_DAYS_MIN = 7;
export const BOOKING_POLLING_INTERVAL_MS = 2_000;

export const DECIMAL_RADIX = 10;
export const CHECKBOX_DEFAULT_SIZE = 16;

/**
 * Keystrokes an integer-only number input must reject: decimal separators, exponent and leading
 * plus. `-` stays allowed so integer fields that accept negative values keep working.
 */
export const NON_INTEGER_NUMBER_KEYS: ReadonlySet<string> = new Set([
  ".",
  ",",
  "e",
  "E",
  "+",
]);

export const FETCH_RIDE_NEARBY_EXCLUDED_STATUSES: readonly RideStatusEnum[] = [
  RideStatusEnum.Canceled,
  RideStatusEnum.Rejected,
  RideStatusEnum.Done,
];

export const ENTITY_DELETE_RELATIONS_CONFIRM_LIST: readonly RydeUiUnit[] = [
  "systemTag",
  "journey",
];

export const ENTITY_ALLOWED_EDIT_ROUTE_POLYLINE_LIST: readonly RydeUiUnit[] = [
  "course",
];

export const ENTITY_SEND_MESSAGE_VIA_PHONE_CELL_CONFIRM_LIST: readonly RydeUiUnit[] =
  ["passenger", "driver"];

// Home-side fields only; the edit-booking pickup/dropoff depend on the direction, see useCanQuickAddStation.
export const DROPDOWN_BOUND_QUICK_ADD_STATION_ALLOWED_LIST: readonly RydeUiUnitDropdownCacheKey[] =
  [
    `station_sourceStation1Id`,
    `station_originStationIdA`,
    `station_destinationStationIdB`,
  ];

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const MAP_DEFAULT_CENTER: TLatLngExpression = [32.8068482, 35.0738091];
export const DEFAULT_GOOGLE_MAP_TILE = "roadmap";
export const DEFAULT_TILES_SERVICE = TilesServiceEnum.OSM;
export const MOBILE_BREAKPOINT_NUMBER = 1090;
/** Extra-narrow phones (e.g. hide dense header controls in row-count bulk mode). */
export const NARROW_MOBILE_BREAKPOINT_NUMBER = 440;
/** Row-count bulk mode: hide starred below this width (toolbar overlap). */
export const HEADER_STARRED_ROW_COUNT_HIDE_BREAKPOINT_NUMBER = 780;
export const DRAWER_FOOTER_HEIGHT = 64;
/** Every Ryde deployment stores Israeli phones, so their national form is the one users read and type. */
export const DEFAULT_PHONE_COUNTRY_CODE: CountryCode = "IL";
/** Covers a large company's passengers and drivers; past it, a phone is formatted without being remembered. */
export const PHONE_LOCAL_FORMAT_CACHE_MAX_ENTRIES = 50_000;
export const ASTRIX_SYMBOL = "*";
export const PATH_WILDCARD = ASTRIX_SYMBOL;
export const RIDE_COURSE_CONVERT_TO_DYNAMIC_FLAG = -1;
export const INVISIBLE_FORM_TAB_LABEL = "INVISIBLE_TAB";
export const LEFT_BAR_WIDTH = 62;

export const LEFT_TO_RIGHT_MARK = "\u200E"; // LEFT-TO-RIGHT MARK (LRM)
export const RIGHT_TO_LEFT_MARK = "\u200F"; // RIGHT-TO-LEFT MARK (RLM)
export const LEFT_TO_RIGHT_EMBEDDING = "\u202A"; // LEFT-TO-RIGHT EMBEDDING (LRE)
export const RIGHT_TO_LEFT_EMBEDDING = "\u202B"; // RIGHT-TO-LEFT EMBEDDING (RLE)
export const POP_DIRECTIONAL_FORMATTING = "\u202C"; // POP DIRECTIONAL FORMATTING (PDF)
export const LEFT_TO_RIGHT_ISOLATE = "\u2066"; // LEFT-TO-RIGHT ISOLATE (LRI)
export const RIGHT_TO_LEFT_ISOLATE = "\u2067"; // RIGHT-TO-LEFT ISOLATE (RLI)
export const FIRST_STRONG_ISOLATE = "\u2068"; // FIRST STRONG ISOLATE (FSI)
export const POP_DIRECTIONAL_ISOLATE = "\u2069"; // POP DIRECTIONAL ISOLATE (PDI)

export const ZERO_WIDTH_SPACE = "\u200B"; // ZERO WIDTH SPACE (ZWSP)
export const ZERO_WIDTH_NON_JOINER = "\u200C"; // ZERO WIDTH NON-JOINER (ZWNJ)
export const ZERO_WIDTH_JOINER = "\u200D"; // ZERO WIDTH JOINER (ZWJ)
export const BYTE_ORDER_MARK = "\uFEFF"; // BYTE ORDER MARK (BOM)

const RTL_MARKERS = `${LEFT_TO_RIGHT_MARK}${RIGHT_TO_LEFT_MARK}${LEFT_TO_RIGHT_EMBEDDING}${RIGHT_TO_LEFT_EMBEDDING}${POP_DIRECTIONAL_FORMATTING}${LEFT_TO_RIGHT_ISOLATE}${RIGHT_TO_LEFT_ISOLATE}${FIRST_STRONG_ISOLATE}${POP_DIRECTIONAL_ISOLATE}`;
const ZERO_WIDTH_MARKS = `${ZERO_WIDTH_SPACE}${ZERO_WIDTH_NON_JOINER}${ZERO_WIDTH_JOINER}${BYTE_ORDER_MARK}`;

export const RTL_MARKERS_REGEX = new RegExp(`[${RTL_MARKERS}]`, "g");
// Every character that is invisible on screen but still breaks an equality check - the
// bidi markers plus the zero-width ones a spreadsheet export leaves inside header cells.
export const INVISIBLE_MARKS_REGEX = new RegExp(
  `[${RTL_MARKERS}${ZERO_WIDTH_MARKS}]`,
  "g",
);
export const WHITESPACE_SEQUENCE_REGEX = /\s+/g;

export const OSRM_ENDPOINT = "/info/osrm/route/v1";
export const FILTER_MULTISELECT_MAX_WIDTH = 600;
export const PERIOD_GROUPING_FIELD_NAME = "period_grouping";
export const DELAY_BEFORE_AUTO_CLOSE_DRAWER = 1000;
export const TOOLTIP_DELAY_BUTTONS = 800;
export const TRANSLATION_KEY = "translationKey";
export const TRANSLATION_PARAMS_KEY = "translationParams";
export const DEFAULT_PASSENGER_SEATS_COUNT = 1;

export const WEBSOCKET_TIMEOUT_MS = 120_000; // default 20_000
export const RIDE_REVERSE_SUFFIX = "reverse";
export const DEFAULT_CHATBOT_FRAME_URL =
  "https://poc-cloud-run-450734231534.europe-central2.run.app/iframe";
export const RIDE_NOT_REVERSED_PARAM_VALUE = "0";
export const RIDE_REVERSED_PARAM_VALUE = "1";

export const ABORT_ERROR_EXCEPTION_NAME = "AbortError";
export const CANCELED_ERROR_EXCEPTION_NAME = "CanceledError";
export const DEFAULT_LOGIN_AUTH_TYPES = Object.values(LoginAuthType);
export const ADD_OR_EDIT_ROUTE_REGEX = /\/(edit|add)/;
export const DEFAULT_SYSTEM_SETTING_SKIP_PREVIEW_STEPS_IF_NO_ERRORS_AT_BOOKINGS = false;

export const DEFAULT_VERSION_CHECK_PATH = "/version.json";
export const MAIN_JS_PATH = "/content/react/main.js";
export const BUILD_VERSION_CHECK_INTERVAL = 300_000;

/**
 * How long the translation catalogue stays valid in localStorage (6h).
 * Backstop only — a backend deploy invalidates it immediately, see `syncI18nCacheWithApiVersion`.
 */
export const I18N_CACHE_EXPIRATION_MS = 21_600_000;
/**
 * Local/Jenkins expiry (1 min) — those environments never bump the API version, so editing
 * a translation file must still show up on the next reload. See `getI18nCacheExpirationMs`.
 */
export const I18N_CACHE_EXPIRATION_DEV_MS = 60_000;
/** localStorage key prefix used by i18next-localstorage-backend for cached bundles. */
export const I18N_CACHE_KEY_PREFIX = "i18next_res_";

export const FORM_VISIBLE_CONDITION_UNDEFINED_TOKEN = "UNDEFINED_TOKEN";
export const UNDEFINED_VALUE = "undefined";
export const NULL_VALUE = "null";

export const PASSWORD_COMPLEXITY_REGEX =
  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;
export const PASSWORD_COMPLEXITY_MIN_LENGTH = 8;

export const LOGS_COLUMN_KEY = "LOGS";
export const BULK_FORM_NAME_PREFIX: TBulkFormPrefix = "bulk";
/** Collapse process successful-id chips by default when the list is longer than this. */
export const PROCESS_SUCCESS_IDS_DEFAULT_COLLAPSED_FROM = 20;
/** Collapse process skipped section by default when skipped count is at least this. */
export const PROCESS_SKIPPED_IDS_DEFAULT_COLLAPSED_FROM = 30;

export const ID_FIELD_NAME = "id";

export const SHOW_TOKEN_EXP_DEBOUNCE_MS = 3000;
export const ID_SEARCH_PATTERN_TEXT_PREFIX = "id:";
export const I18N_DEFAULT_NAMESPACE = "general";
/** The pre-login bundle, served without a session; the full catalogue (`general`) needs one. */
export const I18N_PUBLIC_NAMESPACE = "public";
export const I18N_PUBLIC_BUNDLE_PATH_SEGMENT = "/i18n/public/";
export const AUTH_SET_WINDOW_RELOAD_DELAY_MS = 300;
export const AUTH_MFA_RESEND_COOLDOWN_SECONDS = 60;
export const TOKEN_REFRESH_MARGIN_SECONDS = 60;

/** Per-user card order and per-card settings for the AI conversation stats page. */
export const AI_STATS_LAYOUT_STORAGE_KEY = "ryde.aiConversationStats.layout.v1";

// version displayed for the applicatvion in browser's title and settings form header
export const APP_VERSION = "3.3.7";

export const RETURN_PATH_EXCLUSIONS = ["login", "pagenotfound"];

export const CSV_ESCAPABLE_CHARS_REGEX = /[",\n]/;

export const HEX_COLOR_PATTERN = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const COMPANY_SELECT_ALLOWED_SUBDOMAIN_TOKENS = [
  "jenkins",
  "-temp-",
  "-tests",
  "-staging",
  "temp-web",
  "test-web",
];

/**
 * Host labels that are shared multi-tenant entry points rather than a company code.
 * A company code sent from one of these hosts matches no company on the backend.
 */
export const RESERVED_NON_COMPANY_SUBDOMAINS = ["app", "admin"];

export const INDEX_NOT_FOUND = -1;

export const SKIP_HUB_ID = -1;

export const GET_GEO_AREAS_GEOM_DELAY_MS = 400;
export const SUPPORTED_CSV_MIME_TYPES = ["text/csv", "application/csv"];
export const SUPPORTED_EXCEL_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];
// Extensions accepted by the generic upload flow. Detection is extension-based
// because browsers report inconsistent MIME types for CSV/Excel files
// (e.g. CSV is often sent as "application/vnd.ms-excel"). Add a new entry here
// and a matching parser to support additional formats in the future.
export const SUPPORTED_UPLOAD_FILE_EXTENSIONS = [".csv", ".xlsx", ".xls"];
// Same reasoning for the CSV-only bookings upload: Excel-authored CSVs reach the browser
// as "application/vnd.ms-excel", so the MIME type alone rejects legitimate files.
export const SUPPORTED_CSV_FILE_EXTENSIONS = ["csv"];
export const SUPPORTED_UPLOAD_FILE_ACCEPT =
  SUPPORTED_UPLOAD_FILE_EXTENSIONS.join(",");
export const MIN_FILE_NAME_PARTS_WITH_EXTENSION = 2;
export const UTF8_ENCODING = "utf-8";
// Excel on a Hebrew Windows machine saves CSV as windows-1255, not UTF-8. Reading those
// bytes as UTF-8 turns every Hebrew header and value into mojibake, so an upload silently
// matches nothing.
export const HEBREW_LEGACY_ENCODING = "windows-1255";
// windows-1255 places the Hebrew alphabet in 0xE0-0xFA ("א" to "ת"), which is how a legacy
// Hebrew file is told apart from any other non-UTF-8 content.
export const CP1255_HEBREW_FIRST_BYTE = 0xe0;
export const CP1255_HEBREW_LAST_BYTE = 0xfa;
// How much of a file is inspected to decide its encoding, and in what steps. Bounded on
// purpose: the parser reads the file again afterwards, so this scan stays cheap.
export const ENCODING_DETECTION_CHUNK_SIZE = 65536; // 64 KB
export const ENCODING_DETECTION_MAX_BYTES = 1048576; // 1 MB
export const BOOKINGS_PREPARE_CHUNK_SIZE = 4000;

export const EXCEL_SHEET_NAME_INVALID_CHARS = /[\\/?*[\]:]/g;
export const EXCEL_SHEET_NAME_MAX_LENGTH = 31;

// Rows processed per batch when collecting export data, yielding to the browser
// between batches to keep the UI responsive on large selections.
export const GENERIC_EXPORT_BATCH_SIZE = 20;
export const COMPANY_ID_PATH_REGEX = /^\/c\/(\d+)(?:\/|$|\?)/;

/** Divisor for halving a value (e.g. the midpoint between two coordinates). */
export const HALF_FACTOR = 2;

/** Minimum length so a sequence has an interior between start and end. */
export const MIN_ARRAY_LENGTH_FOR_INNER_CALCULATION = 3;

/** Two routing waypoints can swap relative order even with only start + end stations. */
export const MIN_WAYPOINTS_COUNT_FOR_REORDER = 2;

/**
 * Rectangle address search falls back to when the field carries no seeded default.
 *
 * Mirrors the backend `ADDRESS_SEARCH_DEFAULT_BOUNDS`, so "Reset to default" still has
 * something to restore on a form that was served without `newItemValue`. Keep the two in sync.
 */
export const ADDRESS_SEARCH_DEFAULT_BOUNDS: IAddressSearchBounds = {
  north: 33.35,
  south: 29.45,
  east: 35.95,
  west: 34.2,
};

/** Numeric key for company-wide system params in aggregated store maps (per-site rows use real siteId). */
export const SYSTEM_PARAMS_STORE_ROOT_SITE_ID = 0;
